import React, { useState } from 'react';
import { ImageMedia } from '../../types';

interface ImageDisplayProps {
  image: ImageMedia;
  className?: string;
}

const ImageDisplay: React.FC<ImageDisplayProps> = ({ image, className = '' }) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleImageClick = () => {
    setIsFullScreen(true);
  };

  const handleCloseFullScreen = () => {
    setIsFullScreen(false);
  };

  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  const handleImageError = () => {
    setHasError(true);
  };

  if (hasError) {
    return (
      <div className={`rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center ${className}`}>
        <div className="text-gray-500 p-4 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-8 w-8 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p>Failed to load image</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative rounded-lg overflow-hidden ${className}`}>
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="animate-pulse flex space-x-2">
            <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
            <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
            <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
          </div>
        </div>
      )}
      
      <img
        src={image.url}
        alt={image.alt || 'Chat image'}
        className={`max-w-full rounded-lg cursor-pointer transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleImageClick}
        onLoad={handleImageLoad}
        onError={handleImageError}
        width={image.width}
        height={image.height}
      />
      
      {image.caption && (
        <div className="mt-1 text-sm text-gray-500 italic">
          {image.caption}
        </div>
      )}
      
      {isFullScreen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4" onClick={handleCloseFullScreen}>
          <div className="max-w-full max-h-full">
            <img 
              src={image.url} 
              alt={image.alt || 'Chat image fullscreen'} 
              className="max-w-full max-h-[90vh] object-contain"
            />
            
            {image.caption && (
              <div className="mt-2 text-center text-white">
                {image.caption}
              </div>
            )}
            
            <button 
              className="absolute top-4 right-4 text-white p-2 rounded-full hover:bg-gray-800"
              onClick={handleCloseFullScreen}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageDisplay; 